import styled, { css } from 'styled-components';

import {
    TStyledNavigationItemProperties,
    TStyledTitleProperties,
    TStyledListProperties,
} from './navigation-item.types';

export const StyledListItem = styled.li`
    position: relative;
    &:last-of-type {
        > ul {
            right: -0.2rem;
        }
    }
`;

export const StyledNavigationItem = styled.a<TStyledNavigationItemProperties>`
    position: relative;
    display: flex;
    flex-flow: row;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    min-height: 5.6rem;
    padding: 1rem 1.2rem;
    color: ${({ theme }) => theme.color.black};
    font-weight: ${({ theme }) => theme.font.weight.bold};
    font-size: ${({ theme }) => theme.font.size.base};
    line-height: 2.4rem;
    text-decoration: none;
    background: ${({ theme }) => theme.color.white};
    border: none;
    border-bottom: 0.1rem solid ${({ theme }) => theme.color.grey4};
    cursor: pointer;

    ${({ level, isStandalone }) => {
        if (level === 1) {
            if (isStandalone) {
                return css`
                    cursor: initial;
                    border-color: ${({ theme }) => theme.color.grey6};
                `;
            }

            return;
        }

        if (isStandalone) {
            return css`
                font-weight: ${({ theme }) => theme.font.weight.regular};

                ${StyledMobileArrowIconContainer} {
                    display: none;
                }
            `;
        } else {
            return css`
                font-weight: ${({ theme }) => theme.font.weight.regular};
                background: ${({ theme }) => theme.color.grey2};
            `;
        }
    }};

    &:hover,
    &:focus {
        ${({ level, isStandalone }) => {
            if (level === 1 && isStandalone) {
                return;
            }

            return css`
                background: ${({ theme }) => theme.color.darkPurple};

                * {
                    color: ${({ theme }) => theme.color.white};
                    fill: ${({ theme }) => theme.color.white};
                }
            `;
        }}
    }

    &:focus {
        z-index: 10;
        outline: 0.2rem solid ${({ theme }) => theme.color.purpleTint20};
    }

    @media (min-width: ${({ theme }) =>
            theme.deprecatedScreen.tabletLandscapeMinimum}) {
        ${({ level }) => {
            if (level === 1) {
                return css`
                    border: none;
                    color: ${({ theme }) => theme.color.grey7};
                `;
            }
        }}

        ${({ isStandalone }) => {
            if (isStandalone) {
                return css`
                    ${StyledIconContainer},
                    ${StyledDesktopArrowIconContainer} {
                        display: none !important;
                    }
                `;
            }
        }}

        ${({ isExpanded, level, theme, isStandalone }) => {
            if (!isExpanded) {
                return css`
                    background: ${theme.color.white};
                `;
            }

            if (isStandalone) {
                if (level === 2) {
                    return;
                }

                return css`
                    box-shadow: ${({ theme }) => theme.shadow.high};
                    background: ${theme.color.white};
                    border: 0.1rem solid ${({ theme }) => theme.color.grey5};
                    border-bottom: none;
                `;
            } else {
                /* istanbul ignore next */
                if (level === 2) {
                    return css`
                        background: ${theme.color.grey2};
                    `;
                }

                return css`
                    background: ${theme.color.darkPurple};

                    * {
                        color: ${theme.color.white};
                        fill: ${theme.color.white};
                    }
                `;
            }
        }};

        &:focus {
            outline: none;
            box-shadow: 0 0 0 0.2rem ${({ theme }) => theme.color.white},
                0 0 0 0.4rem ${({ theme }) => theme.color.purpleTint20};
        }
    }
`;

const iconCss = css`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 1.6rem;
    min-height: 1.6rem;
    margin: 0;
    margin-right: 1.6rem;
    padding: 0;
    background: transparent;
    border: none;
    box-shadow: none;
`;

export const StyledIconContainer = styled.span`
    ${iconCss};

    @media (min-width: ${({ theme }) =>
            theme.deprecatedScreen.tabletLandscapeMinimum}) {
        display: none;
    }
`;

export const StyledMobileArrowIconContainer = styled.span`
    ${iconCss};

    @media (min-width: ${({ theme }) =>
            theme.deprecatedScreen.tabletLandscapeMinimum}) {
        display: none;
    }
`;

export const StyledDesktopArrowIconContainer = styled.span`
    ${iconCss};
    display: none;
    margin-right: 0;
    margin-left: 0.4rem;

    @media (min-width: ${({ theme }) =>
            theme.deprecatedScreen.tabletLandscapeMinimum}) {
        display: flex;
    }
`;

export const StyledList = styled.ul<TStyledListProperties>`
    margin: 0;
    padding: 0;
    list-style: none;
    background: ${({ theme }) => theme.color.white};

    @media (min-width: ${({ theme }) =>
            theme.deprecatedScreen.tabletLandscapeMinimum}) {
        position: absolute;
        min-width: 24rem;
        border: 1px solid ${({ theme }) => theme.color.grey5};
        box-shadow: ${({ theme }) => theme.shadow.high};

        li:last-child div {
            border: none;
        }

        ${({ isStandalone }) => {
            if (!isStandalone) {
                return;
            }

            return css`
                border-top: 1px solid ${({ theme }) => theme.color.grey4};
                position: relative;
            `;
        }}
    }
`;

export const StyledTitle = styled.span<TStyledTitleProperties>`
    ${({ level, isStandalone }) => {
        if (!isStandalone) {
            return;
        }

        if (level === 2) {
            return css`
                font-weight: ${({ theme }) => theme.font.weight.regular};
            `;
        }

        return css`
            font-weight: ${({ theme }) => theme.font.weight.bold};
        `;
    }}
`;
